import {
  parseBlockSpec,
  serializeBlockSpec,
  blockSpecsEqual
} from "./blockSpec.ts";
import type {
  BlockSpecInput,
  BlockState
} from "./blockSpec.ts";
import { resolveBlockId } from "./blockRegistry.ts";
import {
  cloneBlockEntityData,
  normalizeBlockEntityData
} from "./blockEntityData.ts";
import type {
  BlockEntityData
} from "./blockEntityData.ts";

interface VoxelChange {
  x: number;
  y: number;
  z: number;
  before: string | null;
  after: string | null;
  beforeBlockEntity: BlockEntityData | null;
  afterBlockEntity: BlockEntityData | null;
}

interface VoxelTransaction {
  label: string;
  changes: Map<string, VoxelChange>;
}

function makeVoxelKey(x, y, z) {
  return String(x) + "," + String(y) + "," + String(z);
}

function parseVoxelKey(key) {
  const parts = key.split(",");
  return {
    x: Number(parts[0]),
    y: Number(parts[1]),
    z: Number(parts[2])
  };
}

function blockEntityEqual(
  first: BlockEntityData | null,
  second: BlockEntityData | null
): boolean {
  return JSON.stringify(first) === JSON.stringify(second);
}

function normalizeCoordinate(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new Error("Voxel coordinates must be finite numbers.");
  }
  return Math.round(parsed);
}

function serializeVersionAwareBlockSpec(
  blockInput: BlockSpecInput,
  explicitState: Record<string, unknown> = {}
): string | null {
  const parsed = parseBlockSpec(blockInput, explicitState);
  if (!parsed) {
    return null;
  }

  return serializeBlockSpec({
    blockId: resolveBlockId(parsed.blockId),
    state: parsed.state
  });
}

export class VoxelWorld {
  blocks: Map<string, string>;
  blockEntities: Map<string, BlockEntityData>;
  listeners: Set<() => void>;
  undoStack: Array<{ label: string; changes: VoxelChange[] }>;
  redoStack: Array<{ label: string; changes: VoxelChange[] }>;
  activeTransaction: VoxelTransaction | null;
  historySuspended: boolean;
  batchDepth: number;
  pendingNotification: boolean;
  minecraftVersion: string | null;

  constructor() {
    this.blocks = new Map();
    this.blockEntities = new Map();
    this.listeners = new Set();
    this.undoStack = [];
    this.redoStack = [];
    this.activeTransaction = null;
    this.historySuspended = false;
    this.batchDepth = 0;
    this.pendingNotification = false;
    this.minecraftVersion = null;
  }

  setMinecraftVersion(versionId: string | null): void {
    this.minecraftVersion = versionId;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    if (this.batchDepth > 0) {
      this.pendingNotification = true;
      return;
    }
    this.listeners.forEach(function (listener) {
      listener();
    });
  }

  beginBatch() {
    this.batchDepth += 1;
  }

  endBatch() {
    this.batchDepth = Math.max(0, this.batchDepth - 1);
    if (this.batchDepth === 0 && this.pendingNotification) {
      this.pendingNotification = false;
      this.notify();
    }
  }

  runBatch(callback) {
    this.beginBatch();
    try {
      return callback();
    } finally {
      this.endBatch();
    }
  }

  beginTransaction(label) {
    if (this.activeTransaction) {
      return;
    }
    this.activeTransaction = {
      label: label || "Edit",
      changes: new Map<string, VoxelChange>()
    };
  }

  commitTransaction() {
    if (!this.activeTransaction) {
      return 0;
    }

    const finishedTransaction = this.activeTransaction;
    this.activeTransaction = null;

    const changes = Array.from(
      finishedTransaction.changes.values()
    ).filter(function (change) {
      return (
        change.before !== change.after ||
        !blockEntityEqual(
          change.beforeBlockEntity,
          change.afterBlockEntity
        )
      );
    });

    if (changes.length > 0 && !this.historySuspended) {
      this.undoStack.push({
        label: finishedTransaction.label,
        changes
      });
      if (this.undoStack.length > 80) {
        this.undoStack.shift();
      }
      this.redoStack.length = 0;
    }

    return changes.length;
  }

  cancelTransaction() {
    if (!this.activeTransaction) {
      return;
    }

    const changes = Array.from(this.activeTransaction.changes.values()).reverse();
    this.activeTransaction = null;

    this.historySuspended = true;
    this.runBatch(() => {
      changes.forEach((change) => {
        this.setBlockRaw(change.x, change.y, change.z, change.before);
        this.setBlockEntityDataRaw(
          change.x,
          change.y,
          change.z,
          change.beforeBlockEntity
        );
      });
    });
    this.historySuspended = false;
  }

  recordChange(
    x,
    y,
    z,
    before,
    after,
    beforeBlockEntity = this.getBlockEntityData(x, y, z),
    afterBlockEntity = beforeBlockEntity
  ) {
    if (!this.activeTransaction || this.historySuspended) {
      return;
    }

    const key = makeVoxelKey(x, y, z);
    const existing = this.activeTransaction.changes.get(key);

    if (existing) {
      existing.after = after;
      existing.afterBlockEntity =
        cloneBlockEntityData(afterBlockEntity);
      return;
    }

    this.activeTransaction.changes.set(key, {
      x,
      y,
      z,
      before,
      after,
      beforeBlockEntity:
        cloneBlockEntityData(beforeBlockEntity),
      afterBlockEntity:
        cloneBlockEntityData(afterBlockEntity)
    });
  }

  getBlockSpec(x, y, z) {
    return this.blocks.get(makeVoxelKey(
      normalizeCoordinate(x),
      normalizeCoordinate(y),
      normalizeCoordinate(z)
    )) || null;
  }

  getBlockData(x, y, z) {
    return parseBlockSpec(this.getBlockSpec(x, y, z));
  }

  getBlock(x, y, z) {
    const data = this.getBlockData(x, y, z);
    return data ? data.blockId : null;
  }

  getBlockState(x, y, z) {
    const data = this.getBlockData(x, y, z);
    return data ? { ...data.state } : {};
  }

  hasBlock(x, y, z) {
    return this.getBlockSpec(x, y, z) !== null;
  }

  getBlockEntityData(
    x,
    y,
    z
  ): BlockEntityData | null {
    const key = makeVoxelKey(
      normalizeCoordinate(x),
      normalizeCoordinate(y),
      normalizeCoordinate(z)
    );
    return cloneBlockEntityData(
      this.blockEntities.get(key) ?? null
    );
  }

  setBlockEntityDataRaw(
    x,
    y,
    z,
    data: BlockEntityData | null | undefined
  ): void {
    const normalizedX = normalizeCoordinate(x);
    const normalizedY = normalizeCoordinate(y);
    const normalizedZ = normalizeCoordinate(z);
    const key = makeVoxelKey(
      normalizedX,
      normalizedY,
      normalizedZ
    );
    const normalized = normalizeBlockEntityData(data);

    if (!normalized || !this.hasBlock(
      normalizedX,
      normalizedY,
      normalizedZ
    )) {
      this.blockEntities.delete(key);
    } else {
      this.blockEntities.set(key, normalized);
    }

    this.notify();
  }

  setBlockEntityData(
    x,
    y,
    z,
    data: BlockEntityData | null | undefined
  ): boolean {
    const normalizedX = normalizeCoordinate(x);
    const normalizedY = normalizeCoordinate(y);
    const normalizedZ = normalizeCoordinate(z);

    if (!this.hasBlock(normalizedX, normalizedY, normalizedZ)) {
      return false;
    }

    const before = this.getBlockEntityData(
      normalizedX,
      normalizedY,
      normalizedZ
    );
    const after = normalizeBlockEntityData(data);

    if (blockEntityEqual(before, after)) {
      return false;
    }

    const blockSpec = this.getBlockSpec(
      normalizedX,
      normalizedY,
      normalizedZ
    );
    this.recordChange(
      normalizedX,
      normalizedY,
      normalizedZ,
      blockSpec,
      blockSpec,
      before,
      after
    );
    this.setBlockEntityDataRaw(
      normalizedX,
      normalizedY,
      normalizedZ,
      after
    );
    return true;
  }

  patchBlockEntityData(
    x,
    y,
    z,
    patch: Partial<BlockEntityData>
  ): boolean {
    const current = this.getBlockEntityData(x, y, z) ?? {};
    return this.setBlockEntityData(x, y, z, {
      ...current,
      ...patch
    });
  }

  setBlockRaw(
    x,
    y,
    z,
    blockInput: BlockSpecInput,
    explicitState: Record<string, unknown> = {}
  ) {
    const normalizedX = normalizeCoordinate(x);
    const normalizedY = normalizeCoordinate(y);
    const normalizedZ = normalizeCoordinate(z);
    const key = makeVoxelKey(normalizedX, normalizedY, normalizedZ);
    // History and imported canonical data must retain exact identities.
    const blockSpec = serializeBlockSpec(
      blockInput,
      explicitState
    );

    if (!blockSpec) {
      this.blocks.delete(key);
    } else {
      this.blocks.set(key, blockSpec);
    }

    this.notify();
  }

  setBlock(
    x,
    y,
    z,
    blockInput: BlockSpecInput,
    explicitState: Record<string, unknown> = {},
    resolveAliases = true
  ) {
    const normalizedX = normalizeCoordinate(x);
    const normalizedY = normalizeCoordinate(y);
    const normalizedZ = normalizeCoordinate(z);
    const before = this.getBlockSpec(
      normalizedX,
      normalizedY,
      normalizedZ
    );
    const after = (resolveAliases ? serializeVersionAwareBlockSpec : serializeBlockSpec)(
      blockInput,
      explicitState
    );
    const beforeData = parseBlockSpec(before);
    const afterData = parseBlockSpec(after);
    const beforeBlockEntity = this.getBlockEntityData(
      normalizedX,
      normalizedY,
      normalizedZ
    );
    const preserveBlockEntity =
      beforeData?.blockId &&
      afterData?.blockId &&
      beforeData.blockId === afterData.blockId;
    const afterBlockEntity = preserveBlockEntity
      ? beforeBlockEntity
      : null;

    if (
      before === after &&
      blockEntityEqual(beforeBlockEntity, afterBlockEntity)
    ) {
      return false;
    }

    this.recordChange(
      normalizedX,
      normalizedY,
      normalizedZ,
      before,
      after,
      beforeBlockEntity,
      afterBlockEntity
    );
    this.setBlockRaw(normalizedX, normalizedY, normalizedZ, after);
    if (!preserveBlockEntity) {
      this.setBlockEntityDataRaw(
        normalizedX,
        normalizedY,
        normalizedZ,
        null
      );
    }
    return true;
  }

  setBlockState(
    x,
    y,
    z,
    partialState: Partial<BlockState>
  ) {
    const current = this.getBlockData(x, y, z);
    if (!current) {
      return false;
    }

    return this.setBlock(x, y, z, {
      blockId: current.blockId,
      state: {
        ...current.state,
        ...(partialState || {})
      }
    });
  }

  removeBlock(x, y, z) {
    return this.setBlock(x, y, z, null);
  }

  clear() {
    if (this.blocks.size === 0) {
      return;
    }

    this.beginTransaction("Clear world");
    this.runBatch(() => {
      Array.from(this.blocks.keys()).forEach((key) => {
        const coordinate = parseVoxelKey(key);
        this.removeBlock(coordinate.x, coordinate.y, coordinate.z);
      });
    });
    this.commitTransaction();
  }

  fill(x1, y1, z1, x2, y2, z2, blockInput, explicitState) {
    const minX = Math.min(normalizeCoordinate(x1), normalizeCoordinate(x2));
    const maxX = Math.max(normalizeCoordinate(x1), normalizeCoordinate(x2));
    const minY = Math.min(normalizeCoordinate(y1), normalizeCoordinate(y2));
    const maxY = Math.max(normalizeCoordinate(y1), normalizeCoordinate(y2));
    const minZ = Math.min(normalizeCoordinate(z1), normalizeCoordinate(z2));
    const maxZ = Math.max(normalizeCoordinate(z1), normalizeCoordinate(z2));
    let changed = 0;

    this.runBatch(() => {
      for (let y = minY; y <= maxY; y += 1) {
        for (let z = minZ; z <= maxZ; z += 1) {
          for (let x = minX; x <= maxX; x += 1) {
            if (this.setBlock(x, y, z, blockInput, explicitState)) {
              changed += 1;
            }
          }
        }
      }
    });

    return changed;
  }

  hollowBox(x1, y1, z1, x2, y2, z2, blockInput, thickness) {
    const minX = Math.min(normalizeCoordinate(x1), normalizeCoordinate(x2));
    const maxX = Math.max(normalizeCoordinate(x1), normalizeCoordinate(x2));
    const minY = Math.min(normalizeCoordinate(y1), normalizeCoordinate(y2));
    const maxY = Math.max(normalizeCoordinate(y1), normalizeCoordinate(y2));
    const minZ = Math.min(normalizeCoordinate(z1), normalizeCoordinate(z2));
    const maxZ = Math.max(normalizeCoordinate(z1), normalizeCoordinate(z2));
    const shellThickness = Math.max(1, normalizeCoordinate(thickness || 1));
    let changed = 0;

    this.runBatch(() => {
      for (let y = minY; y <= maxY; y += 1) {
        for (let z = minZ; z <= maxZ; z += 1) {
          for (let x = minX; x <= maxX; x += 1) {
            const onShell =
              x - minX < shellThickness ||
              maxX - x < shellThickness ||
              y - minY < shellThickness ||
              maxY - y < shellThickness ||
              z - minZ < shellThickness ||
              maxZ - z < shellThickness;

            if (onShell && this.setBlock(x, y, z, blockInput)) {
              changed += 1;
            }
          }
        }
      }
    });

    return changed;
  }

  line(x1, y1, z1, x2, y2, z2, blockInput) {
    const start = {
      x: normalizeCoordinate(x1),
      y: normalizeCoordinate(y1),
      z: normalizeCoordinate(z1)
    };
    const end = {
      x: normalizeCoordinate(x2),
      y: normalizeCoordinate(y2),
      z: normalizeCoordinate(z2)
    };
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const deltaZ = end.z - start.z;
    const steps = Math.max(Math.abs(deltaX), Math.abs(deltaY), Math.abs(deltaZ), 1);
    let changed = 0;

    this.runBatch(() => {
      for (let index = 0; index <= steps; index += 1) {
        const progress = index / steps;
        if (this.setBlock(
          Math.round(start.x + deltaX * progress),
          Math.round(start.y + deltaY * progress),
          Math.round(start.z + deltaZ * progress),
          blockInput
        )) {
          changed += 1;
        }
      }
    });

    return changed;
  }

  cylinder(centerX, y1, centerZ, radius, height, blockInput, hollow) {
    const normalizedCenterX = normalizeCoordinate(centerX);
    const normalizedCenterZ = normalizeCoordinate(centerZ);
    const normalizedY = normalizeCoordinate(y1);
    const normalizedRadius = Math.max(0, normalizeCoordinate(radius));
    const normalizedHeight = Math.max(1, normalizeCoordinate(height));
    const radiusSquared = normalizedRadius * normalizedRadius;
    const innerRadiusSquared =
      Math.max(0, normalizedRadius - 1) * Math.max(0, normalizedRadius - 1);
    let changed = 0;

    this.runBatch(() => {
      for (let y = normalizedY; y < normalizedY + normalizedHeight; y += 1) {
        for (let z = -normalizedRadius; z <= normalizedRadius; z += 1) {
          for (let x = -normalizedRadius; x <= normalizedRadius; x += 1) {
            const distanceSquared = x * x + z * z;
            const insideOuter =
              distanceSquared <= radiusSquared + normalizedRadius * 0.45;
            const outsideInner =
              !hollow ||
              distanceSquared >= innerRadiusSquared - normalizedRadius * 0.3;

            if (
              insideOuter &&
              outsideInner &&
              this.setBlock(
                normalizedCenterX + x,
                y,
                normalizedCenterZ + z,
                blockInput
              )
            ) {
              changed += 1;
            }
          }
        }
      }
    });

    return changed;
  }

  sphere(centerX, centerY, centerZ, radius, blockInput, hollow) {
    const normalizedCenterX = normalizeCoordinate(centerX);
    const normalizedCenterY = normalizeCoordinate(centerY);
    const normalizedCenterZ = normalizeCoordinate(centerZ);
    const normalizedRadius = Math.max(1, normalizeCoordinate(radius));
    const outerSquared = normalizedRadius * normalizedRadius;
    const innerRadius = Math.max(0, normalizedRadius - 1.2);
    const innerSquared = innerRadius * innerRadius;
    let changed = 0;

    this.runBatch(() => {
      for (let y = -normalizedRadius; y <= normalizedRadius; y += 1) {
        for (let z = -normalizedRadius; z <= normalizedRadius; z += 1) {
          for (let x = -normalizedRadius; x <= normalizedRadius; x += 1) {
            const distanceSquared = x * x + y * y + z * z;
            const valid =
              distanceSquared <= outerSquared &&
              (!hollow || distanceSquared >= innerSquared);

            if (
              valid &&
              this.setBlock(
                normalizedCenterX + x,
                normalizedCenterY + y,
                normalizedCenterZ + z,
                blockInput
              )
            ) {
              changed += 1;
            }
          }
        }
      }
    });

    return changed;
  }

  replace(fromBlockInput, toBlockInput) {
    const fromSpec = serializeVersionAwareBlockSpec(fromBlockInput);
    const fromData = parseBlockSpec(fromSpec);
    const toSpec = serializeVersionAwareBlockSpec(toBlockInput);
    if (!fromData) {
      return 0;
    }

    let changed = 0;
    this.runBatch(() => {
      Array.from(this.blocks.entries()).forEach(([key, existingSpec]) => {
        const existingData = parseBlockSpec(existingSpec);
        if (!existingData || existingData.blockId !== fromData.blockId) {
          return;
        }

        if (
          Object.keys(fromData.state).length > 0 &&
          !blockSpecsEqual(existingSpec, fromSpec)
        ) {
          return;
        }

        const coordinate = parseVoxelKey(key);
        if (this.setBlock(coordinate.x, coordinate.y, coordinate.z, toSpec)) {
          changed += 1;
        }
      });
    });

    return changed;
  }

  undo() {
    const entry = this.undoStack.pop();
    if (!entry) {
      return false;
    }

    this.historySuspended = true;
    this.runBatch(() => {
      entry.changes.slice().reverse().forEach((change) => {
        this.setBlockRaw(change.x, change.y, change.z, change.before);
        this.setBlockEntityDataRaw(
          change.x,
          change.y,
          change.z,
          change.beforeBlockEntity
        );
      });
    });
    this.historySuspended = false;
    this.redoStack.push(entry);
    return true;
  }

  redo() {
    const entry = this.redoStack.pop();
    if (!entry) {
      return false;
    }

    this.historySuspended = true;
    this.runBatch(() => {
      entry.changes.forEach((change) => {
        this.setBlockRaw(change.x, change.y, change.z, change.after);
        this.setBlockEntityDataRaw(
          change.x,
          change.y,
          change.z,
          change.afterBlockEntity
        );
      });
    });
    this.historySuspended = false;
    this.undoStack.push(entry);
    return true;
  }

  entries() {
    return Array.from(this.blocks.entries()).map(([key, blockSpec]) => {
      const coordinate = parseVoxelKey(key);
      const blockData = parseBlockSpec(blockSpec);
      if (!blockData) {
        throw new Error("Stored voxel block spec resolved to air.");
      }
      return {
        x: coordinate.x,
        y: coordinate.y,
        z: coordinate.z,
        blockId: blockData.blockId,
        state: { ...blockData.state },
        blockSpec,
        blockEntity: this.getBlockEntityData(
          coordinate.x,
          coordinate.y,
          coordinate.z
        )
      };
    });
  }

  getBounds() {
    if (this.blocks.size === 0) {
      return {
        min: { x: -8, y: 0, z: -8 },
        max: { x: 8, y: 12, z: 8 }
      };
    }

    const bounds = {
      min: { x: Infinity, y: Infinity, z: Infinity },
      max: { x: -Infinity, y: -Infinity, z: -Infinity }
    };

    this.blocks.forEach(function (_blockSpec, key) {
      const coordinate = parseVoxelKey(key);
      bounds.min.x = Math.min(bounds.min.x, coordinate.x);
      bounds.min.y = Math.min(bounds.min.y, coordinate.y);
      bounds.min.z = Math.min(bounds.min.z, coordinate.z);
      bounds.max.x = Math.max(bounds.max.x, coordinate.x);
      bounds.max.y = Math.max(bounds.max.y, coordinate.y);
      bounds.max.z = Math.max(bounds.max.z, coordinate.z);
    });

    return bounds;
  }

  toJSON() {
    return {
      format: "minecraft-world-designer",
      version: 4,
      minecraftVersion: this.minecraftVersion,
      blockCount: this.blocks.size,
      blocks: this.entries()
    };
  }

  loadJSON(project, { preserveBlockIds = false } = {}) {
    if (!project || !Array.isArray(project.blocks)) {
      throw new Error("Invalid voxel project.");
    }

    this.minecraftVersion =
      typeof project.minecraftVersion === "string"
        ? project.minecraftVersion
        : this.minecraftVersion;

    this.beginTransaction("Load project");
    this.runBatch(() => {
      Array.from(this.blocks.keys()).forEach((key) => {
        const coordinate = parseVoxelKey(key);
        this.removeBlock(coordinate.x, coordinate.y, coordinate.z);
      });

      project.blocks.forEach((block) => {
        this.setBlock(
          block.x,
          block.y,
          block.z,
          block.blockSpec || block.blockId,
          block.state || {},
          !preserveBlockIds
        );
        if (block.blockEntity) {
          this.setBlockEntityData(
            block.x,
            block.y,
            block.z,
            block.blockEntity
          );
        }
      });
    });
    this.commitTransaction();
  }
}
